/**
 * Get current user from localStorage
 * @returns {Object} User object or empty object if not found
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : {};
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    return {};
  }
};

export const HasAccess = (actions) => {
  const userStr = localStorage.getItem("user");
  if (!userStr) {
    return actions.map(() => undefined);
  }

  const user = JSON.parse(userStr);
  const userRole = user?.role || '';
  const storedRoles = user?.roles || [];

  const superAdminPermission = {
    create: true,
    update: true,
    delete: true,
    view: true,
    share: true,
    import: true,
    export: true,
  };

  // Robust Admin check
  let activeRoleName = null;
  if (user?.activeRoleId) {
    if (typeof user.activeRoleId === 'object' && user.activeRoleId.roleName) {
      activeRoleName = user.activeRoleId.roleName;
    } else if (user.roles && Array.isArray(user.roles)) {
      const activeRoleIdStr = typeof user.activeRoleId === 'object' ? user.activeRoleId._id : user.activeRoleId;
      const foundRole = user.roles.find(r => String(r._id) === String(activeRoleIdStr) || String(r) === String(activeRoleIdStr));
      if (foundRole) activeRoleName = foundRole.roleName;
    }
  }
  if (!activeRoleName && user.role) activeRoleName = user.role;

  const isAdmin =
    (activeRoleName && ['admin', 'super admin', 'superadmin'].includes(activeRoleName.toLowerCase())) ||
    (user.role && ['admin', 'super admin', 'superadmin'].includes(user.role.toLowerCase())) ||
    (user.roles && Array.isArray(user.roles) && user.roles.some(r => {
      const rName = (r.roleName || r.role || '').toLowerCase();
      return ['admin', 'super admin', 'superadmin'].includes(rName);
    }));

  // SuperAdmin has full access - check this first
  if (isAdmin) {
    return actions.map(() => superAdminPermission);
  }

  // Check if user has activeRoleId set
  const activeRoleId = user?.activeRoleId?._id || user?.activeRoleId;

  let rolesToCheck = [];

  if (activeRoleId && storedRoles.length > 0) {
    // NEW BEHAVIOR: If activeRoleId is set, only check the active role
    const activeRole = storedRoles.find((role) => {
      const roleId = role._id || role;
      return roleId.toString() === activeRoleId.toString();
    });

    if (activeRole) {
      // Only use the active role for permission checking
      rolesToCheck = [activeRole];
    } else {
      // Fallback: If activeRoleId doesn't match any role, use all roles (backward compatibility)
      rolesToCheck = storedRoles;
    }
  } else {
    // OLD BEHAVIOR: No activeRoleId set, use all roles (backward compatibility for old users)
    rolesToCheck = storedRoles;
  }

  // Build permissions from the selected roles
  const mergedPermissions = {};

  actions?.forEach((action) => {
    rolesToCheck.forEach((role) => {
      const permission = role?.access?.find((a) => a.title === action);

      if (permission) {
        const { title, ...rest } = permission;

        if (!mergedPermissions[title]) {
          mergedPermissions[title] = { ...rest };
        } else {
          // Merge permissions using OR logic (if multiple roles are being checked)
          Object.keys(rest).forEach((key) => {
            if (mergedPermissions[title][key] !== true) {
              mergedPermissions[title][key] = rest[key];
            }
          });
        }
      }
    });
  });

  const result = actions.map((action) => {
    return mergedPermissions[action];
  });

  return result;
};
